import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, EmptyState } from '@/components/ui-bits';
import { Zap, Plus, X, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const TRIGGERS = [
  { v: 'lead_created', l: 'Cuando se crea un lead' },
  { v: 'lead_updated', l: 'Cuando se actualiza un lead' },
  { v: 'stage_changed', l: 'Cuando cambia la etapa' },
  { v: 'message_received', l: 'Cuando se recibe un mensaje' },
  { v: 'task_overdue', l: 'Cuando vence una tarea' },
  { v: 'no_response_24h', l: 'Sin respuesta en 24h' },
];
const ACTIONS = [
  { v: 'send_whatsapp', l: 'Enviar WhatsApp' },
  { v: 'send_email', l: 'Enviar email' },
  { v: 'move_stage', l: 'Mover de etapa' },
  { v: 'assign', l: 'Asignar a usuario' },
  { v: 'create_task', l: 'Crear tarea' },
  { v: 'add_tag', l: 'Agregar tag' },
  { v: 'webhook', l: 'Llamar webhook' },
];
const OPS = [
  { v: 'eq', l: 'igual a' },
  { v: 'neq', l: 'distinto de' },
  { v: 'contains', l: 'contiene' },
  { v: 'empty', l: 'vacío' },
  { v: 'not_empty', l: 'no vacío' },
  { v: 'gt', l: '>' },
  { v: 'lt', l: '<' },
];

export default function AutomationsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [users, setUsers] = useState([]);
  const [stages, setStages] = useState([]);
  const [waTpls, setWaTpls] = useState([]);
  const [emailTpls, setEmailTpls] = useState([]);

  const load = async () => {
    const { data } = await api.get('/automations');
    setItems(data.automations);
  };
  useEffect(() => {
    load();
    api.get('/admin/users').then((r) => setUsers(r.data.users || [])).catch(() => {});
    api.get('/pipeline').then((r) => setStages((r.data.pipeline?.stages || []).sort((a, b) => a.order - b.order))).catch(() => {});
    api.get('/whatsapp/templates').then((r) => setWaTpls(r.data.templates || [])).catch(() => {});
    api.get('/email/templates').then((r) => setEmailTpls(r.data.templates || [])).catch(() => {});
  }, []);

  const newAuto = () => setEditing({
    name: '',
    trigger: 'lead_created',
    conditions: { match: 'all', rules: [] },
    actions: [{ type: 'create_task', title: 'Primer contacto', due_offset_hours: 2, priority: 'high', task_type: 'call' }],
    enabled: true,
  });

  const save = async () => {
    try {
      if (editing.id) await api.patch(`/automations/${editing.id}`, editing);
      else await api.post('/automations', editing);
      toast.success('Guardado');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };
  const del = async (a) => {
    if (!confirm('Borrar?')) return;
    await api.delete(`/automations/${a.id}`);
    load();
  };
  const addAction = () => setEditing({ ...editing, actions: [...editing.actions, { type: 'add_tag', tag: 'nuevo' }] });
  const removeAction = (i) => setEditing({ ...editing, actions: editing.actions.filter((_, ix) => ix !== i) });
  const setAction = (i, patch) => {
    const a = [...editing.actions];
    a[i] = { ...a[i], ...patch };
    setEditing({ ...editing, actions: a });
  };
  const rules = editing?.conditions?.rules || [];
  const setRules = (next) => setEditing({
    ...editing,
    conditions: { ...(editing.conditions || {}), match: editing.conditions?.match || 'all', rules: next },
  });

  return (
    <div>
      <PageHeader
        title="Automatizaciones"
        description="Trigger → condiciones → acciones. Las condiciones se evalúan de verdad."
        actions={<button data-testid="auto-new" onClick={newAuto} className="btn-primary"><Plus className="h-4 w-4" /> Nueva automatización</button>}
      />
      {items.length === 0 ? (
        <EmptyState icon={Zap} title="Sin automatizaciones" description="Ej: lead creado + etapa X → WhatsApp de bienvenida." cta={<button onClick={newAuto} className="btn-primary">Crear</button>} />
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} data-testid="auto-card" className="surface p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-[hsl(var(--warning))]" />
                    <span className="font-medium">{a.name}</span>
                    <span className="chip">{a.enabled ? 'Activa' : 'Inactiva'}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Trigger: <span className="font-mono">{a.trigger}</span>
                    {' · '}{(a.conditions?.rules || []).length} condición(es)
                    {' · '}{(a.actions || []).length} acción(es)
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing({
                    ...a,
                    conditions: a.conditions?.rules ? a.conditions : { match: 'all', rules: [] },
                  })} className="btn-ghost h-8 px-2 text-xs">Editar</button>
                  <button onClick={() => del(a)} className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold">Automatización</h2>
              <button onClick={() => setEditing(null)} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <label className="block">Nombre<input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input mt-1" /></label>
              <label className="block">Trigger
                <select value={editing.trigger} onChange={(e) => setEditing({ ...editing, trigger: e.target.value })} className="input mt-1">
                  {TRIGGERS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </label>

              <div className="border border-border rounded-[10px] p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Condiciones</span>
                  <div className="flex gap-2 items-center">
                    <select
                      value={editing.conditions?.match || 'all'}
                      onChange={(e) => setEditing({ ...editing, conditions: { ...editing.conditions, match: e.target.value, rules } })}
                      className="input h-8 w-auto text-xs"
                    >
                      <option value="all">Todas (AND)</option>
                      <option value="any">Cualquiera (OR)</option>
                    </select>
                    <button type="button" onClick={() => setRules([...rules, { field: 'stage_id', op: 'eq', value: '' }])} className="btn-ghost h-7 px-2 text-xs">
                      <Plus className="h-3 w-3" /> Regla
                    </button>
                  </div>
                </div>
                {rules.length === 0 && <p className="text-xs text-muted-foreground">Sin reglas = se aplica a todos los leads del trigger.</p>}
                <div className="space-y-2">
                  {rules.map((r, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto_1fr_auto] gap-1 items-center">
                      <select value={r.field} onChange={(e) => { const n = [...rules]; n[i] = { ...n[i], field: e.target.value }; setRules(n); }} className="input h-8 text-xs">
                        <option value="stage_id">Etapa</option>
                        <option value="status">Status</option>
                        <option value="source">Origen</option>
                        <option value="city">Ciudad</option>
                        <option value="assigned_to">Asignado</option>
                        <option value="tags">Tags</option>
                        <option value="email">Email</option>
                        <option value="phone_e164">Teléfono</option>
                        <option value="deal_value">Valor deal</option>
                      </select>
                      <select value={r.op} onChange={(e) => { const n = [...rules]; n[i] = { ...n[i], op: e.target.value }; setRules(n); }} className="input h-8 text-xs w-28">
                        {OPS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                      {r.field === 'stage_id' ? (
                        <select value={r.value || ''} onChange={(e) => { const n = [...rules]; n[i] = { ...n[i], value: e.target.value }; setRules(n); }} className="input h-8 text-xs">
                          <option value="">—</option>
                          {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      ) : r.field === 'assigned_to' ? (
                        <select value={r.value || ''} onChange={(e) => { const n = [...rules]; n[i] = { ...n[i], value: e.target.value }; setRules(n); }} className="input h-8 text-xs">
                          <option value="">—</option>
                          {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                        </select>
                      ) : !['empty', 'not_empty'].includes(r.op) ? (
                        <input value={r.value || ''} onChange={(e) => { const n = [...rules]; n[i] = { ...n[i], value: e.target.value }; setRules(n); }} className="input h-8 text-xs" placeholder="valor" />
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                      <button type="button" onClick={() => setRules(rules.filter((_, ix) => ix !== i))} className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Acciones</span>
                  <button onClick={addAction} className="btn-ghost h-7 px-2 text-xs"><Plus className="h-3 w-3" /> Agregar</button>
                </div>
                <div className="space-y-2 mt-2">
                  {editing.actions.map((act, i) => (
                    <div key={i} className="border border-border rounded-[10px] p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <select value={act.type} onChange={(e) => setAction(i, { type: e.target.value })} className="input flex-1">
                          {ACTIONS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
                        </select>
                        <button onClick={() => removeAction(i)} className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      {act.type === 'send_whatsapp' && (
                        <>
                          <label className="block text-xs">Plantilla
                            <select value={act.template_id || ''} onChange={(e) => setAction(i, { template_id: e.target.value || null })} className="input mt-1">
                              <option value="">— Cuerpo libre —</option>
                              {waTpls.filter((t) => t.enabled !== false).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                          </label>
                          <label className="block text-xs">Cuerpo<textarea value={act.body || ''} onChange={(e) => setAction(i, { body: e.target.value })} className="input mt-1" /></label>
                        </>
                      )}
                      {act.type === 'send_email' && (
                        <>
                          <label className="block text-xs">Plantilla
                            <select value={act.template_id || ''} onChange={(e) => setAction(i, { template_id: e.target.value || null })} className="input mt-1">
                              <option value="">— Libre —</option>
                              {emailTpls.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                          </label>
                          <label className="block text-xs">Asunto<input value={act.subject || ''} onChange={(e) => setAction(i, { subject: e.target.value })} className="input mt-1" /></label>
                          <label className="block text-xs">Cuerpo HTML<textarea value={act.body || ''} onChange={(e) => setAction(i, { body: e.target.value })} className="input mt-1 font-mono text-xs" /></label>
                        </>
                      )}
                      {act.type === 'move_stage' && (
                        <label className="block text-xs">Etapa
                          <select value={act.stage_id || ''} onChange={(e) => setAction(i, { stage_id: e.target.value })} className="input mt-1">
                            <option value="">—</option>
                            {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </label>
                      )}
                      {act.type === 'assign' && (
                        <label className="block text-xs">Usuario
                          <select value={act.user_id || ''} onChange={(e) => setAction(i, { user_id: e.target.value })} className="input mt-1">
                            <option value="">—</option>
                            {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                          </select>
                        </label>
                      )}
                      {act.type === 'create_task' && (
                        <>
                          <label className="block text-xs">Título<input value={act.title || ''} onChange={(e) => setAction(i, { title: e.target.value })} className="input mt-1" /></label>
                          <label className="block text-xs">Horas para vencimiento<input type="number" value={act.due_offset_hours || 24} onChange={(e) => setAction(i, { due_offset_hours: Number(e.target.value) })} className="input mt-1" /></label>
                        </>
                      )}
                      {act.type === 'add_tag' && <label className="block text-xs">Tag<input value={act.tag || ''} onChange={(e) => setAction(i, { tag: e.target.value })} className="input mt-1" /></label>}
                      {act.type === 'webhook' && <label className="block text-xs">URL<input value={act.url || ''} onChange={(e) => setAction(i, { url: e.target.value })} className="input mt-1" /></label>}
                    </div>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} /> Activa
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button>
              <button data-testid="auto-save" onClick={save} className="btn-primary"><Save className="h-4 w-4" /> Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
